'use client';

import React from 'react';
import {
  Calendar,
  CalendarClock,
  Bell,
  ChevronRight,
  Info
} from 'lucide-react';

export interface WhatsAppMessageItem {
  id: string;
  title: string;
  subtitle: string;
  trigger?: string;
  enabled: boolean;
  content: string;
}

interface WhatsAppMessagesListProps {
  messages: WhatsAppMessageItem[];
  onToggleMessage: (id: string) => void;
  onPreviewMessage: (message: WhatsAppMessageItem) => void;
}

export function WhatsAppMessagesList({
  messages,
  onToggleMessage,
  onPreviewMessage
}: WhatsAppMessagesListProps) {
  const getIcon = (title: string) => {
    if (title.includes('agendado')) return Calendar;
    if (title.includes('Actualización')) return CalendarClock;
    return Bell;
  };

  const enabledCount = messages.filter((m) => m.enabled).length;

  return (
    <div className="space-y-6">
      {/* Informational Note */}
      <div className="p-5 bg-white border border-slate-200/90 rounded-2xl shadow-2xs space-y-3 text-xs text-slate-600 leading-relaxed">
        <div className="flex items-center gap-2 font-bold text-slate-900">
          <Info className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>Estos 5 mensajes ya están escritos. Podés mirarlos ahora; empiezan a salir apenas vinculás tu WhatsApp.</span>
        </div>
        <p>
          <strong>Sin botones.</strong> Estos mensajes salen desde tu propio número de WhatsApp, así que llegan como texto: tu cliente te responde por el mismo chat y lo leés vos.
        </p>
        <p className="text-slate-500">
          <strong>Próximamente:</strong> vas a poder editar y personalizar cada mensaje con el nombre y el tono de tu negocio. Por ahora el texto es fijo — podés prender y apagar cada uno, y elegir a qué servicios les sale.
        </p>
      </div>

      {/* Messages Card Container */}
      <div className="bg-white border border-slate-200/90 rounded-3xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 bg-slate-50/70 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
            MENSAJES AUTOMÁTICOS
          </h3>
          <span className="text-xs text-slate-500 font-medium">
            5 listos · {enabledCount} activos
          </span>
        </div>

        <div className="divide-y divide-slate-100">
          {messages.map((msg) => {
            const Icon = getIcon(msg.title);
            return (
              <div
                key={msg.id}
                className="px-6 py-4.5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-50/50 transition-colors"
              >
                {/* Left: Drag Handle, Icon, Title, Subtitle */}
                <div className="flex items-center gap-3.5">
                  <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-700 border border-emerald-100 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>

                  <div>
                    <h4 className="text-sm font-bold text-slate-900">
                      {msg.title}
                    </h4>
                    <p className="text-xs text-slate-500">
                      {msg.subtitle}
                    </p>
                  </div>
                </div>

                {/* Right: Badge Horario, Ver mensaje >, Switch */}
                <div className="flex items-center gap-4 self-end sm:self-center">
                  {msg.trigger && msg.trigger.startsWith('Sale') && (
                    <span className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-semibold">
                      {msg.trigger}
                    </span>
                  )}

                  <button
                    type="button"
                    onClick={() => onPreviewMessage(msg)}
                    className="text-xs font-bold text-slate-600 hover:text-emerald-700 transition-colors flex items-center gap-1"
                  >
                    <span>Ver mensaje</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>

                  <button
                    type="button"
                    onClick={() => onToggleMessage(msg.id)}
                    className={`w-11 h-6 flex items-center rounded-full p-1 cursor-pointer transition-colors ${
                      msg.enabled ? 'bg-emerald-600' : 'bg-slate-300'
                    }`}
                  >
                    <div
                      className={`bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform ${
                        msg.enabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
